import {
  collection,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  updateDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  runTransaction,
  deleteField,
  type Unsubscribe,
  type Timestamp,
} from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import {
  ensureStaffSession,
  findStaffUserData,
  getDb,
  getFirebaseAuth,
  onBeforeClearStaffSession,
  getFirebaseStorage,
  getStaffSession,
  type StaffFirebaseRole,
  type StaffSession,
} from '../firebase'
import { getChatSupportProjectId } from '../config/chatSupportFirebase'
import {
  priorityFromTicket,
  queueIdFromTicket,
  reasonIdFromTicket,
  ticketVisibleToRole,
  type SupportPriority,
  type SupportQueueId,
} from '../utils/supportCategoryUtils'
import { getUserRole } from '../utils/userRoleUtils'
import {
  ensureSupportTicketsLane,
  legacyStaffCollection,
  staffDocsKeyedByEmail,
  supportRibbonDocId,
  supportStaffCollection,
  supportStaffDocumentId,
  supportTicketsCollection,
  ticketMatchesLane,
} from '../utils/supportTicketsCollection'
import { driverSupportDemoStore, isSupportDemoMode } from './driverSupportDemoStore'

export type SupportTicketStatus = 'open' | 'accepted' | 'resolved' | 'closed'
export type SupportAssignmentStatus = 'unassigned' | 'assigned' | 'cancelled'
/** Queue ids — Flutter `category` is a reason, not this. */
export type SupportCategory = SupportQueueId
export type MediaType = 'text' | 'image' | 'video' | 'file' | 'location'

export interface SupportTicketRating {
  stars?: number
  tags?: string[]
  comment?: string
  ratedAt?: Date | null
}

export interface SupportTicket {
  id: string
  ticketNumber: string
  driverId: string
  driverName: string
  driverPhone: string
  userType: string
  category: string
  categoryId: string
  queueId: SupportQueueId
  priority: SupportPriority
  assignmentStatus: SupportAssignmentStatus | string
  subcategory: string
  subject: string
  description: string
  status: SupportTicketStatus
  assignedStaffId?: string | null
  assignedStaffName?: string | null
  assignedStaffRole?: string | null
  assignedConsoleUserId?: string | null
  assignedConsoleEmail?: string | null
  vehiclePlate?: string | null
  vehicleImei?: string | null
  city?: string | null
  partner?: string | null
  createdAt?: Date | null
  updatedAt?: Date | null
  acceptedAt?: Date | null
  resolvedAt?: Date | null
  closedAt?: Date | null
  reopenedAt?: Date | null
  resolutionCode?: string | null
  resolutionNote?: string | null
  firstAgentResponseAt?: Date | null
  rating?: SupportTicketRating | null
  escalated?: boolean
  escalatedAt?: Date | null
  escalatedById?: string | null
  escalatedByName?: string | null
  escalatedByConsoleUserId?: string | null
  escalationTakenAt?: Date | null
  /** Flutter lane marker. Dev tickets are `DEV`. Enterprise tickets omit it. */
  environment?: string | null
}

export interface SupportDeskNote {
  id: string
  authorId: string
  authorName: string
  text: string
  createdAt?: Date | null
}

export interface SupportStaffAgent {
  uid: string
  name: string
  role: StaffFirebaseRole
  supportDeskOnline?: boolean
  lastSeenAt?: Date | null
  /** When this visible session started. Heartbeats do not move it. */
  deskOnlineSince?: Date | null
  maxConcurrent?: number
  consoleUserId?: string
  consoleEmail?: string
  consoleRole?: string
  /** Auth ids this same person has used. Dev keeps them on the one email document. */
  firebaseUids?: string[]
}

export interface SupportMessage {
  id: string
  senderId: string
  senderName: string
  senderType: 'driver' | 'staff'
  text: string
  mediaType?: MediaType | string
  mediaUrl?: string
  fileName?: string
  mimeType?: string
  latitude?: number
  longitude?: number
  createdAt?: Date | null
}

function toDate(v: unknown): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  if (typeof (v as Timestamp).toDate === 'function') return (v as Timestamp).toDate()
  return null
}

function toNum(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v)
  return undefined
}

function normalizeStatus(raw: unknown): SupportTicketStatus {
  const s = String(raw || 'open')
    .toLowerCase()
    .trim()
  if (s === 'accepted' || s === 'resolved' || s === 'closed' || s === 'open') return s
  return 'open'
}

function mapRating(raw: unknown): SupportTicketRating | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  const nested =
    d.feedback && typeof d.feedback === 'object' && !Array.isArray(d.feedback)
      ? (d.feedback as Record<string, unknown>)
      : null
  const stars = toNum(d.stars) ?? toNum(d.score) ?? toNum(d.rating)
  const tagRaw = d.tags ?? d.positiveTags ?? d.goodTags ?? d.feedbackTags
  const tags = Array.isArray(tagRaw) ? tagRaw.map((t) => String(t)).filter(Boolean) : undefined
  const commentKeys = [
    'comment',
    'note',
    'notes',
    'improvement',
    'improve',
    'improveComment',
    'text',
    'message',
  ] as const
  let comment: string | undefined
  for (const key of commentKeys) {
    const v = d[key]
    if (typeof v === 'string' && v.trim()) {
      comment = v.trim()
      break
    }
  }
  if (!comment && typeof d.feedback === 'string' && d.feedback.trim()) comment = d.feedback.trim()
  if (!comment && nested) {
    for (const key of commentKeys) {
      const v = nested[key]
      if (typeof v === 'string' && v.trim()) {
        comment = v.trim()
        break
      }
    }
  }
  if (stars == null && !tags?.length && !comment) return null
  return {
    stars,
    tags,
    comment,
    ratedAt: toDate(d.ratedAt) || toDate(d.createdAt),
  }
}

function mapTicket(id: string, d: Record<string, unknown>): SupportTicket {
  const short = id.length >= 6 ? id.slice(0, 6).toUpperCase() : id.toUpperCase()
  const assignedRaw = d.assignedStaffId
  const assignedStaffId =
    assignedRaw === undefined || assignedRaw === null || assignedRaw === '' ? null : String(assignedRaw)
  const reason = reasonIdFromTicket(d)
  const queueId = queueIdFromTicket(d)
  const assignmentStatus = String(d.assignmentStatus || (assignedStaffId ? 'assigned' : 'unassigned'))
  return {
    id,
    ticketNumber: (d.ticketNumber as string) || `VALT-${short}`,
    driverId: String(d.driverId || ''),
    driverName: String(d.driverName || 'Driver'),
    driverPhone: String(d.driverPhone || ''),
    userType: String(d.userType || 'driver'),
    category: reason || String(d.category || ''),
    categoryId: reason || String(d.categoryId || d.category || ''),
    queueId,
    priority: priorityFromTicket(d),
    assignmentStatus,
    subcategory: String(d.subcategory || ''),
    subject: String(d.subject || ''),
    description: String(d.description || ''),
    status: normalizeStatus(d.status),
    assignedStaffId,
    assignedStaffName: (d.assignedStaffName as string) || null,
    assignedStaffRole: (d.assignedStaffRole as string) || null,
    assignedConsoleUserId: d.assignedConsoleUserId ? String(d.assignedConsoleUserId) : null,
    assignedConsoleEmail: d.assignedConsoleEmail ? String(d.assignedConsoleEmail).toLowerCase() : null,
    vehiclePlate: d.vehiclePlate ? String(d.vehiclePlate) : null,
    vehicleImei: d.vehicleImei ? String(d.vehicleImei) : null,
    city: d.city ? String(d.city) : null,
    partner: d.partner ? String(d.partner) : null,
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    acceptedAt: toDate(d.acceptedAt),
    resolvedAt: toDate(d.resolvedAt),
    closedAt: toDate(d.closedAt),
    reopenedAt: toDate(d.reopenedAt),
    resolutionCode: d.resolutionCode ? String(d.resolutionCode) : null,
    resolutionNote: d.resolutionNote ? String(d.resolutionNote) : null,
    firstAgentResponseAt: toDate(d.firstAgentResponseAt),
    rating: mapRating(d.rating),
    escalated: Boolean(d.escalated),
    escalatedAt: toDate(d.escalatedAt),
    escalatedById: (d.escalatedById as string) || null,
    escalatedByName: (d.escalatedByName as string) || null,
    escalatedByConsoleUserId: d.escalatedByConsoleUserId ? String(d.escalatedByConsoleUserId) : null,
    escalationTakenAt: toDate(d.escalationTakenAt),
    environment: d.environment ? String(d.environment) : null,
  }
}

function roleLabel(role: StaffFirebaseRole | string) {
  const r = String(role).toLowerCase()
  if (r === 'finance') return 'Finance'
  if (r === 'workshop') return 'Workshop management'
  if (r === 'manager') return 'Manager'
  if (r === 'team_manager') return 'Team Manager'
  return 'Admin'
}

function roleIsEscalationHandler(role?: StaffFirebaseRole | string) {
  const r = String(role || agent().staffRole).toLowerCase()
  return r === 'manager' || r === 'team_manager' || r === 'admin'
}

function consoleAppRole() {
  return String(getUserRole() || '').toLowerCase()
}

/** Console team managers / admins — not Support Agents mapped to Firebase admin. */
function isManagerDeskUser(staffRole?: StaffFirebaseRole | string) {
  const app = consoleAppRole()
  if (app === 'support_agent') return false
  if (app === 'team_manager' || app === 'manager' || app === 'admin' || app === 'org_admin' || app === 'owner') {
    return true
  }
  const r = String(staffRole || agent().staffRole).toLowerCase()
  return r === 'manager' || r === 'team_manager'
}

/** Console admin / owner / manager — can message any live ticket without taking assignment. */
function isAdminDeskUser(staffRole?: StaffFirebaseRole | string) {
  if (isSupportAgent() && !isSupportDemoMode()) return false
  const app = consoleAppRole()
  if (app === 'admin' || app === 'org_admin' || app === 'owner' || app === 'team_manager' || app === 'manager') {
    return true
  }
  if (app === 'support_agent') return false
  const r = String(staffRole || agent().staffRole).toLowerCase()
  return (r === 'admin' || r === 'manager' || r === 'team_manager') && isManagerDeskUser(staffRole)
}

function canAdminReplyOnTicket(ticket: SupportTicket | null, staffRole?: StaffFirebaseRole | string) {
  if (!ticket) return false
  if (ticket.status === 'closed' || ticket.status === 'resolved') return false
  return isAdminDeskUser(staffRole)
}

function consoleRoleDisplay() {
  const app = consoleAppRole()
  if (app === 'owner') return 'Owner'
  if (app === 'org_admin') return 'Org Admin'
  if (app === 'admin') return 'Admin'
  if (app === 'manager') return 'Manager'
  if (app === 'team_manager') return 'Team Manager'
  return roleLabel(agent().staffRole)
}

function adminDisplayName(staffName: string) {
  const me = consoleAssignee()
  if (me.name && me.name !== 'Console Agent') return me.name
  const n = staffName.trim()
  return n || 'Admin'
}

function adminReplyHeading(staffName: string) {
  return `${adminDisplayName(staffName)} (${consoleRoleDisplay()}) replied`
}

function signedAdminReply(staffName: string, text: string) {
  const head = adminReplyHeading(staffName)
  const body = text.trim()
  if (!body) return head
  if (body.toLowerCase().startsWith(head.toLowerCase())) return body
  return `${head}\n${body}`
}

function isSupportAgent() {
  if (isSupportDemoMode()) return true
  return consoleAppRole() === 'support_agent'
}

/** Support agents and management desks both publish presence. Routing still requires support_agent. */
function keepsDeskPresence() {
  return isSupportAgent() || isManagerDeskUser()
}

function isRoutableSupportAgent(person: SupportStaffAgent) {
  if (person.uid === agent().staffUid) return isSupportAgent()
  return String(person.consoleRole || '').toLowerCase() === 'support_agent'
}

function escalationClaimedByHandler(ticket: SupportTicket) {
  if (ticket.escalationTakenAt) return true
  const ar = String(ticket.assignedStaffRole || '').toLowerCase()
  return ar === 'manager' || ar === 'team_manager'
}

function canActOnTicket(ticket: SupportTicket | null, staffUid: string, staffRole: StaffFirebaseRole) {
  if (!ticket) return false
  if (ticket.status === 'closed') return false
  if (ticket.escalated) {
    if (!isManagerDeskUser(staffRole)) return false
    if (!escalationClaimedByHandler(ticket)) return false
    return ticketOwnedByCurrentConsoleUser(ticket, staffUid)
  }
  return ticketOwnedByCurrentConsoleUser(ticket, staffUid)
}

function canReassignTicket(ticket: SupportTicket | null, staffRole?: StaffFirebaseRole | string) {
  if (!ticket) return false
  if (ticket.escalated) return false
  if (ticket.status === 'closed' || ticket.status === 'resolved') return false
  if (ticket.assignmentStatus === 'cancelled') return false
  if (isSupportAgent()) return !!ticket.assignedStaffId
  return isManagerDeskUser(staffRole)
}

function canTakeEscalationTicket(ticket: SupportTicket | null, _staffUid: string, staffRole: StaffFirebaseRole) {
  if (!ticket) return false
  if (!isManagerDeskUser(staffRole)) return false
  if (!ticket.escalated) return false
  if (ticket.status === 'closed' || ticket.status === 'resolved') return false
  if (ticket.assignmentStatus === 'cancelled') return false
  if (escalationClaimedByHandler(ticket)) return false
  return true
}

function agentEscalatedThis(ticket: SupportTicket, staffUid: string) {
  if (!ticket.escalated) return false
  if (ticket.escalatedById && ticket.escalatedById === staffUid) return true
  const me = consoleAssignee()
  if (me.id && ticket.escalatedByConsoleUserId) return ticket.escalatedByConsoleUserId === me.id
  return false
}

function agent(): StaffSession {
  return (
    getStaffSession() || {
      staffUid: '',
      staffName: 'Console Agent',
      staffRole: 'admin',
      isDemo: false,
    }
  )
}

function consoleAssignee(): { id: string; email: string; name: string } {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}') as Record<string, unknown>
    const first = String(u.first_name || '').trim()
    const last = String(u.last_name || '').trim()
    const name = [first, last].filter(Boolean).join(' ') || String(u.name || u.email || 'Console Agent')
    return {
      id: u.id != null ? String(u.id) : '',
      email: String(u.email || '')
        .trim()
        .toLowerCase(),
      name,
    }
  } catch {
    return { id: '', email: '', name: 'Console Agent' }
  }
}

async function consoleIdentityForAssignee(
  target: { uid: string; consoleUserId?: string; consoleEmail?: string },
  actor: StaffSession,
): Promise<{ id: string | null; email: string | null }> {
  if (target.uid === actor.staffUid) {
    const me = consoleAssignee()
    return { id: me.id || null, email: me.email || null }
  }
  let id = target.consoleUserId ? String(target.consoleUserId) : ''
  let email = target.consoleEmail ? String(target.consoleEmail).trim().toLowerCase() : ''
  if (!id && !email) {
    try {
      const d = await findStaffUserData(target.uid, target.consoleEmail)
      if (d) {
        id = d.consoleUserId != null ? String(d.consoleUserId) : ''
        email = d.consoleEmail ? String(d.consoleEmail).trim().toLowerCase() : ''
      }
    } catch {
      /* keep empty — ownership then falls back to assignedStaffId */
    }
  }
  return { id: id || null, email: email || null }
}

function isPlaceholderAgentName(name: string) {
  const n = name.trim().toLowerCase()
  return !n || n === 'console agent' || n === 'staff' || n === 'agent'
}

/** Menu label. A stored "Staff" name falls back to the console email and does not replace a real name. */
function transferSeatLabel(person: SupportStaffAgent) {
  const name = person.name.trim()
  if (!isPlaceholderAgentName(name)) return name
  return person.consoleEmail?.trim() || ''
}

function preferAssignSeat(next: SupportStaffAgent, prev: SupportStaffAgent) {
  const nextFresh = isFreshOnline(next)
  const prevFresh = isFreshOnline(prev)
  if (nextFresh !== prevFresh) return nextFresh
  return (next.lastSeenAt?.getTime() || 0) > (prev.lastSeenAt?.getTime() || 0)
}

function isLiveDeskAgentTarget(person: SupportStaffAgent, assignedStaffId?: string | null) {
  if (person.uid === assignedStaffId) return false
  const cr = String(person.consoleRole || '').toLowerCase()
  if (cr === 'manager' || cr === 'team_manager' || cr === 'admin' || cr === 'org_admin' || cr === 'owner') {
    return false
  }
  if (cr === 'support_agent') return true
  const r = String(person.role || '').toLowerCase()
  return r === 'admin' || r === 'finance' || r === 'workshop'
}

/** Old tickets claimed under the shared demo_admin alias are not personal Mine. */
function isSharedDemoAssignment(ticket: SupportTicket): boolean {
  if (ticket.assignedConsoleUserId || ticket.assignedConsoleEmail) return false
  return ticket.assignedStaffId === 'demo_admin'
}

function ticketOwnedByCurrentConsoleUser(ticket: SupportTicket, staffUid: string): boolean {
  const me = consoleAssignee()
  if (me.id && ticket.assignedConsoleUserId) return ticket.assignedConsoleUserId === me.id
  if (me.email && ticket.assignedConsoleEmail) return ticket.assignedConsoleEmail === me.email
  if (isSharedDemoAssignment(ticket)) return false
  return !!ticket.assignedStaffId && ticket.assignedStaffId === staffUid
}

function safeFileName(name: string) {
  return name.replace(/[^\w.-]+/g, '_').slice(0, 120) || 'file'
}

function guessMediaType(file: File): MediaType {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  return 'file'
}

function stripVoiceCallCopy(text: string) {
  return String(text || '')
    .replace(/\s*Voice call is available\.?/gi, '')
    .replace(/\s*A voice call is available\.?/gi, '')
    .trim()
}

function mapMessage(id: string, data: Record<string, unknown>): SupportMessage {
  const st = String(data.senderType || 'staff')
  return {
    id,
    senderId: String(data.senderId || ''),
    senderName: String(data.senderName || ''),
    senderType: st === 'driver' ? 'driver' : 'staff',
    text: stripVoiceCallCopy(String(data.text || '')),
    mediaType: data.mediaType ? String(data.mediaType) : undefined,
    mediaUrl: data.mediaUrl ? String(data.mediaUrl) : undefined,
    fileName: data.fileName ? String(data.fileName) : undefined,
    mimeType: data.mimeType ? String(data.mimeType) : undefined,
    latitude: toNum(data.latitude),
    longitude: toNum(data.longitude),
    createdAt: toDate(data.createdAt),
  }
}

function hasDriverRating(ticket: SupportTicket) {
  const stars = Number(ticket.rating?.stars)
  return Number.isFinite(stars) && stars > 0
}

const ratingCloseLocks = new Set<string>()
const REOPEN_NOTICE = 'Driver reopened this ticket.'

function finishedAtMs(source: { resolvedAt?: Date | null; closedAt?: Date | null }) {
  return Math.max(source.resolvedAt?.getTime() || 0, source.closedAt?.getTime() || 0)
}

async function driverMessagedAfter(ticketId: string, finishedMs: number) {
  const snap = await getDocs(query(ticketMessages(ticketId), orderBy('createdAt', 'desc'), limit(20)))
  return snap.docs.some((d) => {
    const data = d.data() as Record<string, unknown>
    if (String(data.senderType || '') !== 'driver') return false
    const at = toDate(data.createdAt)?.getTime() || 0
    return at > finishedMs
  })
}

const PRESENCE_HEARTBEAT_MS = 60_000

let devStaffDocsConsolidated = false

function staffUidList(data: Record<string, unknown>, docId: string): string[] {
  const ids = new Set<string>()
  const current = data.firebaseUid != null ? String(data.firebaseUid).trim() : ''
  if (current) ids.add(current)
  if (Array.isArray(data.firebaseUids)) {
    for (const id of data.firebaseUids) {
      const value = String(id || '').trim()
      if (value) ids.add(value)
    }
  }
  if (docId && !docId.includes('@')) ids.add(docId)
  return [...ids]
}

/**
 * One staff document per console email on Dev, QA, and Enterprise. Newer sign-ins update
 * that document. Random id documents are folded in, then removed.
 */
async function consolidateDevStaffDocs() {
  const colName = supportStaffCollection()
  if (!staffDocsKeyedByEmail(colName)) return
  const snap = await getDocs(collection(getDb(), colName))
  const groups = new Map<string, typeof snap.docs>()
  const orphans: typeof snap.docs = []
  for (const staffDoc of snap.docs) {
    const data = staffDoc.data() as Record<string, unknown>
    const fromField = String(data.consoleEmail || '')
      .trim()
      .toLowerCase()
    const fromId = staffDoc.id.includes('@') ? staffDoc.id.trim().toLowerCase() : ''
    const email = fromField || fromId
    if (!email || email.includes('/')) {
      orphans.push(staffDoc)
      continue
    }
    const list = groups.get(email) || []
    list.push(staffDoc)
    groups.set(email, list)
  }

  for (const [email, docs] of groups) {
    let newest = docs[0]!
    let newestMs = -1
    for (const staffDoc of docs) {
      const data = staffDoc.data() as Record<string, unknown>
      const seen = toDate(data.lastSeenAt)?.getTime() || 0
      if (seen >= newestMs) {
        newest = staffDoc
        newestMs = seen
      }
    }
    const canonical = docs.find((staffDoc) => staffDoc.id === email)
    const newestData = newest.data() as Record<string, unknown>
    const storedUid = newestData.firebaseUid != null ? String(newestData.firebaseUid).trim() : ''
    const payload: Record<string, unknown> = { ...newestData }
    payload.consoleEmail = email
    payload.firebaseUid = storedUid || (newest.id.includes('@') ? '' : newest.id)
    payload.firebaseUids = deleteField()
    if (!canonical || canonical.id !== newest.id) {
      await setDoc(doc(getDb(), colName, email), payload, { merge: true })
    } else {
      await setDoc(
        doc(getDb(), colName, email),
        {
          consoleEmail: email,
          firebaseUid: payload.firebaseUid,
          firebaseUids: deleteField(),
        },
        { merge: true },
      )
    }
    await Promise.all(
      docs
        .filter((staffDoc) => staffDoc.id !== email)
        .map((staffDoc) => deleteDoc(staffDoc.ref).catch(() => undefined)),
    )
  }

  await Promise.all(orphans.map((staffDoc) => deleteDoc(staffDoc.ref).catch(() => undefined)))
}

const activeDeskSessions = new Set<string>()

function staffUidFromDoc(id: string, data: Record<string, unknown>): string {
  const stored = data.firebaseUid != null ? String(data.firebaseUid).trim() : ''
  return stored || id
}

function mapStaffAgent(id: string, data: Record<string, unknown>): SupportStaffAgent {
  const roleRaw = String(data.role || 'admin').toLowerCase()
  const role: StaffFirebaseRole =
    roleRaw === 'finance' ||
    roleRaw === 'workshop' ||
    roleRaw === 'admin' ||
    roleRaw === 'manager' ||
    roleRaw === 'team_manager'
      ? roleRaw
      : 'admin'
  const max = Number(data.maxConcurrent)
  const uid = staffUidFromDoc(id, data)
  return {
    uid,
    name: String(data.name || data.displayName || id),
    role,
    supportDeskOnline: Boolean(data.supportDeskOnline),
    lastSeenAt: toDate(data.lastSeenAt),
    deskOnlineSince: toDate(data.deskOnlineSince),
    maxConcurrent: Number.isFinite(max) && max > 0 ? max : 5,
    consoleUserId: data.consoleUserId != null ? String(data.consoleUserId) : '',
    consoleEmail: data.consoleEmail
      ? String(data.consoleEmail).toLowerCase()
      : id.includes('@')
        ? id.toLowerCase()
        : '',
    consoleRole: data.consoleRole ? String(data.consoleRole).toLowerCase() : '',
    firebaseUids: staffUidList(data, id),
  }
}

const routeLocks = new Set<string>()
const STALE_MS = 2 * 60 * 1000
/** Used when app_config/config_vars.devAwayMs is missing or below 1 second. */
const DEFAULT_DEV_AWAY_MS = 5 * 60 * 1000
const DEV_STAFF_EMAIL_KEY = 'valt_dev_staff_email'
let devAwayMs = DEFAULT_DEV_AWAY_MS
let devAwayLoadedAt = 0

function parseDevAwayMs(raw: unknown): number | null {
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1000) return null
  return n
}

/** Live offline window from app_config/config_vars. Cached for 15s so a console edit applies on the next sweep. */
async function loadDevAwayMs(): Promise<number> {
  const now = Date.now()
  if (now - devAwayLoadedAt < 15_000) return devAwayMs
  try {
    const snap = await getDoc(doc(getDb(), 'app_config', 'config_vars'))
    const next = parseDevAwayMs(snap.get('devAwayMs'))
    if (next) devAwayMs = next
  } catch {
    /* keep the last good value */
  }
  devAwayLoadedAt = Date.now()
  return devAwayMs
}

function compareRibbon(a: SupportStaffAgent, b: SupportStaffAgent) {
  const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  if (byName !== 0) return byName
  return a.uid.localeCompare(b.uid)
}

/** One seat per person. A fresh online doc wins over an older duplicate. */
function ribbonRoster(agents: SupportStaffAgent[]): SupportStaffAgent[] {
  const bySeat = new Map<string, SupportStaffAgent>()
  for (const person of agents) {
    if (!isRoutableSupportAgent(person)) continue
    const key = (person.consoleEmail || person.uid).toLowerCase()
    const prev = bySeat.get(key)
    if (!prev) {
      bySeat.set(key, person)
      continue
    }
    const personFresh = isFreshOnline(person)
    const prevFresh = isFreshOnline(prev)
    if (personFresh && !prevFresh) {
      bySeat.set(key, person)
      continue
    }
    if (personFresh === prevFresh && (person.lastSeenAt?.getTime() || 0) > (prev.lastSeenAt?.getTime() || 0)) {
      bySeat.set(key, person)
    }
  }
  return [...bySeat.values()].sort(compareRibbon)
}

function ribbonCircle(ticket: SupportTicket): 'admin' | 'onboarding' {
  const reason = reasonIdFromTicket(ticket)
  if (reason === 'wantToJoin' || ticket.queueId === 'onboarding') return 'onboarding'
  return 'admin'
}

function seatKey(person: SupportStaffAgent) {
  return (person.consoleEmail || person.uid).toLowerCase()
}

function nextOnRibbon(
  roster: SupportStaffAgent[],
  lastId: string,
  lastEmail: string,
  agents: SupportStaffAgent[],
  isEligible: (person: SupportStaffAgent) => boolean,
): SupportStaffAgent | null {
  if (roster.length === 0) return null
  const knownEmail = (lastEmail || agents.find((person) => person.uid === lastId)?.consoleEmail || '').toLowerCase()
  let start = 0
  if (lastId || knownEmail) {
    const idx = roster.findIndex((person) => person.uid === lastId || (knownEmail && seatKey(person) === knownEmail))
    start = idx >= 0 ? idx + 1 : 0
  }
  for (let i = 0; i < roster.length; i++) {
    const person = roster[(start + i) % roster.length]
    if (person && isEligible(person)) return person
  }
  return null
}

function presenceStaleMs() {
  return staffDocsKeyedByEmail() ? devAwayMs : STALE_MS
}

function isFreshOnline(person: SupportStaffAgent) {
  if (activeDeskSessions.has(person.uid)) return true
  const seen = person.lastSeenAt?.getTime() || 0
  return Boolean(seen && Date.now() - seen <= presenceStaleMs())
}

/** Display only. A hidden tab stays assignable until presenceStaleMs; this just paints that gap orange. */
const PRESENCE_AWAY_VIEW_MS = PRESENCE_HEARTBEAT_MS + 30_000

export type DeskPresenceView = 'online' | 'away' | 'offline'

/** Last seen is the source of truth. A cleared online flag must not paint a fresh heartbeat red. */
function viewDeskPresence(person: SupportStaffAgent, now = Date.now()): DeskPresenceView {
  const seen = person.lastSeenAt?.getTime() || 0
  const age = seen ? now - seen : Number.POSITIVE_INFINITY
  if (!seen || age > presenceStaleMs()) return 'offline'
  if (age > PRESENCE_AWAY_VIEW_MS) return 'away'
  return 'online'
}

/** Open work follows the person, including tickets still held on an older login. */
function activeLoad(
  tickets: SupportTicket[],
  person: SupportStaffAgent,
  agents: SupportStaffAgent[],
  pendingLoads?: Map<string, number>,
) {
  const key = seatKey(person)
  const uids = new Set<string>()
  for (const other of agents) {
    if (seatKey(other) !== key) continue
    uids.add(other.uid)
    for (const id of other.firebaseUids || []) uids.add(id)
  }
  uids.add(person.uid)
  for (const id of person.firebaseUids || []) uids.add(id)
  const email = person.consoleEmail?.trim().toLowerCase() || ''
  const held = tickets.filter((ticket) => {
    if (ticket.status !== 'accepted' && ticket.status !== 'open') return false
    if (email && ticket.assignedConsoleEmail) return ticket.assignedConsoleEmail.toLowerCase() === email
    return !!ticket.assignedStaffId && uids.has(ticket.assignedStaffId)
  }).length
  return held + (pendingLoads?.get(key) || 0)
}

async function loadStaffForRouting(): Promise<SupportStaffAgent[]> {
  await ensureSupportTicketsLane()
  await loadDevAwayMs()
  const me = agent()
  const consoleMe = consoleAssignee()
  const map = new Map<string, SupportStaffAgent>()
  map.set(me.staffUid, {
    uid: me.staffUid,
    name: me.staffName,
    role: me.staffRole,
    supportDeskOnline: isSupportAgent(),
    lastSeenAt: new Date(),
    maxConcurrent: 5,
    consoleUserId: consoleMe.id,
    consoleEmail: consoleMe.email,
    consoleRole: consoleAppRole(),
  })
  try {
    const snap = await getDocs(collection(getDb(), supportStaffCollection()))
    snap.docs.forEach((d) => {
      const person = mapStaffAgent(d.id, d.data() as Record<string, unknown>)
      map.set(person.uid, person)
    })
  } catch {
    /* keep current agent */
  }
  const cur = map.get(me.staffUid)
  if (cur && activeDeskSessions.has(me.staffUid)) {
    cur.supportDeskOnline = isSupportAgent()
    cur.consoleRole = consoleAppRole()
    cur.consoleUserId = cur.consoleUserId || consoleMe.id
    cur.consoleEmail = cur.consoleEmail || consoleMe.email
  }
  return [...map.values()]
}

function priorityRank(p: SupportPriority) {
  if (p === 'P0') return 0
  if (p === 'P1') return 1
  if (p === 'P2') return 2
  return 3
}

function sortDesk(tickets: SupportTicket[]) {
  return [...tickets].sort((a, b) => {
    const pr = priorityRank(a.priority) - priorityRank(b.priority)
    if (pr !== 0) return pr
    return (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0)
  })
}

function ticketsRoot() {
  return supportTicketsCollection()
}

function subscribeAfterLane(start: () => Unsubscribe): Unsubscribe {
  let stopped = false
  let inner: Unsubscribe = () => undefined
  void ensureSupportTicketsLane().then(() => {
    if (stopped) return
    inner = start()
  })
  return () => {
    stopped = true
    inner()
  }
}

function ticketDoc(ticketId: string, db = getDb()) {
  return doc(db, ticketsRoot(), ticketId)
}

function ticketMessages(ticketId: string, db = getDb()) {
  return collection(db, ticketsRoot(), ticketId, 'messages')
}

function ticketDeskNotes(ticketId: string, db = getDb()) {
  return collection(db, ticketsRoot(), ticketId, 'desk_notes')
}

async function stampFirstAgentResponse(ticketId: string) {
  const ref = ticketDoc(ticketId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return
  if ((snap.data() as Record<string, unknown>).firstAgentResponseAt) return
  await updateDoc(ref, {
    firstAgentResponseAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

function rememberedDevStaffEmail() {
  const live = consoleAssignee().email
  if (live) return live
  try {
    return sessionStorage.getItem(DEV_STAFF_EMAIL_KEY) || ''
  } catch {
    return ''
  }
}

function rememberDevStaffEmail(email: string) {
  if (!email) return
  try {
    sessionStorage.setItem(DEV_STAFF_EMAIL_KEY, email)
  } catch {
    /* ignore */
  }
}

let devAwayTimer: ReturnType<typeof setTimeout> | null = null
let devBeatTimer: ReturnType<typeof setInterval> | null = null
let devSweepTimer: ReturnType<typeof setInterval> | null = null
let devRowUnsub: Unsubscribe | null = null
let devPresenceBound = false
let devSignedOff = false
let devReasserting = false
/** Local time of this browser's last online write. Another laptop's newer lastSeen wins. */
let devLastBeatAt = 0
/** Clock skew between this hide and the server timestamp it writes. */
const PRESENCE_BEAT_SLACK_MS = 5_000

function stopDevBeat() {
  if (devBeatTimer) clearInterval(devBeatTimer)
  devBeatTimer = null
}

let devAwayGen = 0
let devAwayPending = false

function clearDevAway() {
  devAwayGen += 1
  devAwayPending = false
  if (devAwayTimer) clearTimeout(devAwayTimer)
  devAwayTimer = null
}

async function writeDevStaffPresence(online: boolean, stampOnlineSince = false) {
  await ensureSupportTicketsLane()
  const colName = supportStaffCollection()
  if (!staffDocsKeyedByEmail(colName)) return
  const email = rememberedDevStaffEmail()
  if (!email) return
  rememberDevStaffEmail(email)
  if (online && !devSignedOff) await ensureStaffSession()
  const staff = getStaffSession()
  const visible = typeof document !== 'undefined' && document.visibilityState === 'visible' && !devSignedOff
  const payload: Record<string, unknown> = {
    supportDeskOnline: online,
    lastSeenAt: serverTimestamp(),
    consoleEmail: email,
    firebaseUids: deleteField(),
  }
  if (online && stampOnlineSince) payload.deskOnlineSince = serverTimestamp()
  if (online && staff?.staffUid) {
    if (!isPlaceholderAgentName(staff.staffName)) payload.name = staff.staffName
    payload.role = staff.staffRole
    payload.consoleRole = consoleAppRole() || null
    payload.consoleUserId = consoleAssignee().id || null
    payload.maxConcurrent = 5
  }
  if (staff?.staffUid) {
    if (online && visible) activeDeskSessions.add(staff.staffUid)
    else activeDeskSessions.delete(staff.staffUid)
  }
  if (online) devLastBeatAt = Date.now()
  await setDoc(doc(getDb(), colName, email), payload, { merge: true })
  void refreshDevPresenceToken()
  if (online && !devStaffDocsConsolidated) {
    devStaffDocsConsolidated = true
    void consolidateDevStaffDocs().catch(() => {
      devStaffDocsConsolidated = false
    })
  }
}

async function sweepStaleDevAgents() {
  await ensureSupportTicketsLane()
  const colName = supportStaffCollection()
  if (!staffDocsKeyedByEmail(colName)) return
  const snap = await getDocs(collection(getDb(), colName))
  const cutoff = Date.now() - (await loadDevAwayMs())
  await Promise.all(
    snap.docs.map(async (staffDoc) => {
      const data = staffDoc.data() as Record<string, unknown>
      if (!data.supportDeskOnline) return
      const seen = toDate(data.lastSeenAt)?.getTime() || 0
      if (seen > cutoff) return
      await runTransaction(getDb(), async (tx) => {
        const fresh = await tx.get(staffDoc.ref)
        if (!fresh.exists()) return
        const row = fresh.data() as Record<string, unknown>
        if (!row.supportDeskOnline) return
        const seenNow = toDate(row.lastSeenAt)?.getTime() || 0
        if (seenNow > cutoff) return
        tx.update(staffDoc.ref, { supportDeskOnline: false })
      }).catch(() => undefined)
    }),
  )
}

const DEV_STAFF_TOKEN_KEY = 'valt_dev_staff_token'

function stampDevLeaveKeepalive() {
  const email = rememberedDevStaffEmail()
  let token = ''
  try {
    token = sessionStorage.getItem(DEV_STAFF_TOKEN_KEY) || ''
  } catch {
    token = ''
  }
  if (!email || !token) return
  const url =
    `https://firestore.googleapis.com/v1/projects/${getChatSupportProjectId()}` +
    `/databases/(default)/documents/${supportStaffCollection()}/${encodeURIComponent(email)}` +
    '?updateMask.fieldPaths=lastSeenAt&updateMask.fieldPaths=supportDeskOnline'
  fetch(url, {
    method: 'PATCH',
    keepalive: true,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fields: {
        lastSeenAt: { timestampValue: new Date().toISOString() },
        supportDeskOnline: { booleanValue: true },
      },
    }),
  }).catch(() => undefined)
}

async function refreshDevPresenceToken() {
  try {
    const user = getFirebaseAuth().currentUser
    if (!user) return
    const token = await user.getIdToken()
    sessionStorage.setItem(DEV_STAFF_TOKEN_KEY, token)
  } catch {
    /* leave the last token */
  }
}

/**
 * Sets this agent offline only when no other open desk has heartbeated since beatAt.
 * Does not move lastSeenAt, so a visible laptop keeps the row.
 */
async function markOfflineIfNoNewerBeat(beatAt: number) {
  await ensureSupportTicketsLane()
  const colName = supportStaffCollection()
  if (!staffDocsKeyedByEmail(colName)) return
  const email = rememberedDevStaffEmail()
  if (!email) return
  const ref = doc(getDb(), colName, email)
  await runTransaction(getDb(), async (tx) => {
    const snap = await tx.get(ref)
    if (!snap.exists()) return
    const seen = toDate(snap.get('lastSeenAt'))?.getTime() || 0
    if (seen > beatAt + PRESENCE_BEAT_SLACK_MS) return
    tx.update(ref, { supportDeskOnline: false })
  })
}

function stopDevRowWatch() {
  devRowUnsub?.()
  devRowUnsub = null
}

/** A visible page puts its own row back online as soon as another session clears it. */
function watchDevRow() {
  if (devRowUnsub || typeof document === 'undefined') return
  const email = rememberedDevStaffEmail()
  const colName = supportStaffCollection()
  if (!email || !staffDocsKeyedByEmail(colName)) return
  devRowUnsub = onSnapshot(doc(getDb(), colName, email), (snap) => {
    if (devSignedOff || !keepsDeskPresence()) return
    if (document.visibilityState !== 'visible') return
    if (!snap.exists() || snap.get('supportDeskOnline') === true) return
    if (devReasserting) return
    devReasserting = true
    void writeDevStaffPresence(true).finally(() => {
      devReasserting = false
    })
  })
}

function armDevAway() {
  stopDevBeat()
  const staff = getStaffSession()
  if (staff?.staffUid) activeDeskSessions.delete(staff.staffUid)
  if (devAwayTimer || devAwayPending || devSignedOff) return
  devAwayPending = true
  const gen = ++devAwayGen
  const hideAt = Date.now()
  stampDevLeaveKeepalive()
  void writeDevStaffPresence(true)
    .then(async () => {
      if (gen !== devAwayGen || devSignedOff) return hideAt
      const email = rememberedDevStaffEmail()
      const colName = supportStaffCollection()
      if (!email || !staffDocsKeyedByEmail(colName)) return hideAt
      const snap = await getDoc(doc(getDb(), colName, email))
      return toDate(snap.get('lastSeenAt'))?.getTime() || hideAt
    })
    .then(async (beatAt) => {
      const ms = await loadDevAwayMs()
      devAwayPending = false
      if (gen !== devAwayGen || devSignedOff) return
      devAwayTimer = setTimeout(() => {
        devAwayTimer = null
        if (devSignedOff) return
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') return
        void markOfflineIfNoNewerBeat(beatAt)
      }, ms)
    })
    .catch(() => {
      devAwayPending = false
    })
}

function resumeDevPresence() {
  if (devSignedOff || !keepsDeskPresence()) return
  clearDevAway()
  watchDevRow()
  void writeDevStaffPresence(true, true)
  void sweepStaleDevAgents()
  stopDevBeat()
  devBeatTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (devSignedOff) return
    void writeDevStaffPresence(true)
    void sweepStaleDevAgents()
  }, PRESENCE_HEARTBEAT_MS)
}

function ensureDevSweepLoop() {
  if (devSweepTimer || typeof window === 'undefined') return
  devSweepTimer = setInterval(() => {
    void sweepStaleDevAgents()
  }, 30_000)
}

function bindDevPresence() {
  if (devPresenceBound || typeof document === 'undefined') return
  devPresenceBound = true
  document.addEventListener('visibilitychange', () => {
    if (devSignedOff || !keepsDeskPresence()) return
    if (document.visibilityState === 'hidden') armDevAway()
    else resumeDevPresence()
  })
  window.addEventListener('pagehide', () => {
    if (devSignedOff || !keepsDeskPresence()) return
    armDevAway()
  })
}

async function signOffDevSupportAgent() {
  devSignedOff = true
  clearDevAway()
  stopDevBeat()
  if (devSweepTimer) clearInterval(devSweepTimer)
  devSweepTimer = null
  stopDevRowWatch()
  const staff = getStaffSession()
  if (staff?.staffUid) activeDeskSessions.delete(staff.staffUid)
  const beatAt = devLastBeatAt
  await markOfflineIfNoNewerBeat(beatAt)
}

onBeforeClearStaffSession(signOffDevSupportAgent)

export const driverSupportFirestoreService = {
  isDemoMode() {
    return isSupportDemoMode()
  },

  /** Dev and QA sign-off. Writes offline before auth storage is cleared. */
  async signOffSupportAgent() {
    await signOffDevSupportAgent()
  },

  /** Read-only desk colour. Does not change who can receive a ticket. */
  deskPresenceView(person: SupportStaffAgent, now?: number) {
    return viewDeskPresence(person, now)
  },

  /** Support agents and management desks stay online while the console is on screen. */
  syncDevSupportPresence() {
    void ensureSupportTicketsLane().then(() => {
      if (!staffDocsKeyedByEmail()) return
      ensureDevSweepLoop()
      void sweepStaleDevAgents()
      const email = consoleAssignee().email
      if (!keepsDeskPresence() || !email) return
      devSignedOff = false
      rememberDevStaffEmail(email)
      bindDevPresence()
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') armDevAway()
      else resumeDevPresence()
    })
  },

  async ready() {
    if (isSupportDemoMode()) {
      return {
        staffUid: driverSupportDemoStore.staffUid,
        staffName: driverSupportDemoStore.staffName,
        staffRole: driverSupportDemoStore.staffRole,
        isDemo: true,
      } satisfies StaffSession
    }
    return ensureStaffSession()
  },

  async assertCanAct(ticketId: string, staff: StaffSession) {
    const ticket = await this.loadTicket(ticketId)
    if (canActOnTicket(ticket, staff.staffUid, staff.staffRole)) return
    if (canAdminReplyOnTicket(ticket, staff.staffRole)) return
    throw new Error('This escalation is view-only until a team manager assigns it to themselves.')
  },

  async assertCanCompose(ticketId: string, staff: StaffSession) {
    const ticket = await this.loadTicket(ticketId)
    if (canActOnTicket(ticket, staff.staffUid, staff.staffRole)) return ticket
    if (canAdminReplyOnTicket(ticket, staff.staffRole)) return ticket
    throw new Error('Only the assigned person can reply, unless you are an admin or manager.')
  },

  async loadTicket(ticketId: string): Promise<SupportTicket | null> {
    if (isSupportDemoMode()) return driverSupportDemoStore.getTicket(ticketId)
    await ensureSupportTicketsLane()
    const snap = await getDoc(ticketDoc(ticketId))
    if (!snap.exists()) return null
    const mapped = mapTicket(snap.id, snap.data() as Record<string, unknown>)
    return ticketMatchesLane(mapped) ? mapped : null
  },

  currentStaff() {
    if (isSupportDemoMode()) {
      return {
        staffUid: driverSupportDemoStore.staffUid,
        staffName: driverSupportDemoStore.staffName,
        staffRole: driverSupportDemoStore.staffRole,
        isDemo: true,
      } satisfies StaffSession
    }
    return agent()
  },

  watchTickets(onData: (tickets: SupportTicket[]) => void, onError?: (e: Error) => void): Unsubscribe {
    if (isSupportDemoMode()) {
      return driverSupportDemoStore.watchTickets(onData)
    }
    return subscribeAfterLane(() => {
      const col = collection(getDb(), ticketsRoot())
      return onSnapshot(
        col,
        { includeMetadataChanges: true },
        (snap) => {
          if (snap.metadata.fromCache && snap.empty) return
          const list = snap.docs
            .map((d) => mapTicket(d.id, d.data() as Record<string, unknown>))
            .filter((t) => ticketMatchesLane(t))
          onData(sortDesk(list))
          void driverSupportFirestoreService.syncFinishedTickets(list)
        },
        (err) => {
          const msg = String((err as Error)?.message || err)
          if (/Firestore API has not been used|firestore\.googleapis\.com/i.test(msg)) {
            onError?.(
              new Error(
                `Cloud Firestore API is disabled for project ${getChatSupportProjectId()}. Enable it in Google Cloud / Firebase Console.`,
              ),
            )
            return
          }
          onError?.(err as Error)
        },
      )
    })
  },

  watchTicket(ticketId: string, onData: (t: SupportTicket | null) => void): Unsubscribe {
    if (isSupportDemoMode()) {
      return driverSupportDemoStore.watchTicket(ticketId, onData)
    }
    return subscribeAfterLane(() =>
      onSnapshot(ticketDoc(ticketId), (snap) => {
        if (!snap.exists()) {
          onData(null)
          return
        }
        const mapped = mapTicket(snap.id, snap.data() as Record<string, unknown>)
        if (!ticketMatchesLane(mapped)) {
          onData(null)
          return
        }
        onData(mapped)
        void driverSupportFirestoreService.syncFinishedTickets([mapped])
      }),
    )
  },

  watchMessages(ticketId: string, onData: (msgs: SupportMessage[]) => void): Unsubscribe {
    if (isSupportDemoMode()) {
      return driverSupportDemoStore.watchMessages(ticketId, onData)
    }
    return subscribeAfterLane(() => {
      const col = ticketMessages(ticketId)
      const q = query(col, orderBy('createdAt', 'asc'))
      return onSnapshot(q, (snap) => {
        onData(snap.docs.map((d) => mapMessage(d.id, d.data() as Record<string, unknown>)))
      })
    })
  },

  async assignTicket(ticketId: string) {
    const staff = await this.ready()
    return this.assignTicketTo(ticketId, {
      uid: staff.staffUid,
      name: staff.staffName,
      role: staff.staffRole,
    })
  },

  async assignTicketTo(
    ticketId: string,
    target: {
      uid: string
      name: string
      role: StaffFirebaseRole | string
      consoleUserId?: string
      consoleEmail?: string
    },
  ) {
    await ensureSupportTicketsLane()
    const actor = await this.ready()
    const role = (String(target.role || 'admin').toLowerCase() || 'admin') as StaffFirebaseRole
    const managerAssigningAgent = isManagerDeskUser(actor.staffRole) && !isSupportAgent()
    if (!isSupportAgent() && !managerAssigningAgent) {
      throw new Error('Only a support agent or manager can assign Driver Support Chat tickets.')
    }
    if (managerAssigningAgent && target.uid === actor.staffUid) {
      throw new Error('Managers assign live tickets to support agents. Use Assign to me only for escalations.')
    }
    if (isSupportDemoMode()) {
      const demo = driverSupportDemoStore.getTicket(ticketId)
      if (demo?.escalated) {
        throw new Error('This ticket is escalated. A team manager must assign it from Support Chat Management.')
      }
      if (!isSupportAgent() && !managerAssigningAgent) {
        throw new Error('Only a support agent or manager can assign Driver Support Chat tickets.')
      }
      driverSupportDemoStore.assign(
        ticketId,
        { uid: target.uid, name: target.name, role: String(role) },
        { staffUid: actor.staffUid, staffName: actor.staffName },
      )
      return
    }
    const existing = await getDoc(ticketDoc(ticketId))
    const prev = existing.exists() ? (existing.data() as Record<string, unknown>) : {}
    if (existing.exists() && !ticketMatchesLane({ environment: prev.environment ? String(prev.environment) : null })) {
      throw new Error('This ticket belongs to another environment.')
    }
    if (prev.escalated) {
      throw new Error('This ticket is escalated. A team manager must assign it from Support Chat Management.')
    }
    const prevId = prev.assignedStaffId ? String(prev.assignedStaffId) : ''
    const isTransfer = !!prevId && prevId !== target.uid
    const targetConsole = await consoleIdentityForAssignee(target, actor)

    await updateDoc(ticketDoc(ticketId), {
      status: 'accepted',
      assignmentStatus: 'assigned',
      assignedStaffId: target.uid,
      assignedStaffName: target.name,
      assignedStaffRole: role,
      assignedConsoleUserId: targetConsole.id,
      assignedConsoleEmail: targetConsole.email,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    const text = isTransfer
      ? `${actor.staffName} transferred this ticket to ${target.name}.`
      : `${target.name} (${roleLabel(role)}) accepted this ticket. You can chat now.${
          actor.staffUid !== target.uid ? ` Assigned by ${actor.staffName}.` : ''
        }`

    await addDoc(ticketMessages(ticketId), {
      senderId: actor.staffUid,
      senderName: actor.staffName,
      senderType: 'staff',
      text,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
  },

  async escalateTicket(ticketId: string) {
    const staff = await this.ready()
    if (isSupportDemoMode()) {
      driverSupportDemoStore.escalate(ticketId, staff)
      return
    }
    const me = consoleAssignee()
    await updateDoc(ticketDoc(ticketId), {
      escalated: true,
      escalatedAt: serverTimestamp(),
      escalatedById: staff.staffUid,
      escalatedByName: staff.staffName,
      escalatedByConsoleUserId: me.id || null,
      escalationTakenAt: deleteField(),
      status: 'accepted',
      assignmentStatus: 'unassigned',
      assignedStaffId: null,
      assignedStaffName: null,
      assignedStaffRole: null,
      assignedConsoleUserId: null,
      assignedConsoleEmail: null,
      updatedAt: serverTimestamp(),
    })
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName: staff.staffName,
      senderType: 'staff',
      text: `${staff.staffName} escalated this ticket to Team Manager. A team manager can assign it to themselves to chat and close.`,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
  },

  watchNotes(ticketId: string, onData: (notes: SupportDeskNote[]) => void): Unsubscribe {
    if (isSupportDemoMode()) {
      return driverSupportDemoStore.watchNotes(ticketId, onData)
    }
    return subscribeAfterLane(() => {
      const col = ticketDeskNotes(ticketId)
      return onSnapshot(col, (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>
          return {
            id: d.id,
            authorId: String(data.authorId || ''),
            authorName: String(data.authorName || 'Staff'),
            text: String(data.text || ''),
            createdAt: toDate(data.createdAt),
          }
        })
        list.sort((a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0))
        onData(list)
      })
    })
  },

  async addNote(ticketId: string, text: string) {
    const staff = await this.ready()
    const trimmed = text.trim()
    if (!trimmed) return
    const ticket = await this.loadTicket(ticketId)
    if (ticket?.status === 'closed') return
    if (
      ticket?.escalated &&
      !canActOnTicket(ticket, staff.staffUid, staff.staffRole) &&
      !canAdminReplyOnTicket(ticket, staff.staffRole)
    ) {
      throw new Error('This escalation is view-only until a team manager assigns it to themselves.')
    }
    if (isSupportDemoMode()) {
      driverSupportDemoStore.addNote(ticketId, trimmed, staff)
      return
    }
    await addDoc(ticketDeskNotes(ticketId), {
      authorId: staff.staffUid,
      authorName: staff.staffName,
      text: trimmed,
      createdAt: serverTimestamp(),
    })
    await updateDoc(ticketDoc(ticketId), {
      updatedAt: serverTimestamp(),
    })
  },

  watchStaffAgents(onData: (agents: SupportStaffAgent[]) => void): Unsubscribe {
    if (isSupportDemoMode()) {
      const agents = driverSupportDemoStore.listAgents()
      onData(agents)
      return () => undefined
    }
    const me = agent()
    return subscribeAfterLane(() => {
      const col = collection(getDb(), supportStaffCollection())
      return onSnapshot(
        col,
        (snap) => {
          const map = new Map<string, SupportStaffAgent>()
          map.set(me.staffUid, {
            uid: me.staffUid,
            name: me.staffName,
            role: me.staffRole,
            supportDeskOnline: false,
            lastSeenAt: null,
            consoleUserId: consoleAssignee().id,
            consoleEmail: consoleAssignee().email,
            consoleRole: consoleAppRole(),
          })
          snap.docs.forEach((d) => {
            const person = mapStaffAgent(d.id, d.data() as Record<string, unknown>)
            map.set(person.uid, person)
          })
          const list = [...map.values()].sort((a, b) => a.uid.localeCompare(b.uid))
          onData(list)
        },
        () => {
          const fallback = [{ uid: me.staffUid, name: me.staffName, role: me.staffRole }]
          onData(fallback)
        },
      )
    })
  },

  async setSupportDeskPresence(online: boolean) {
    const staff = await this.ready()
    if (online) activeDeskSessions.add(staff.staffUid)
    else activeDeskSessions.delete(staff.staffUid)
    if (isSupportDemoMode()) {
      driverSupportDemoStore.setPresence(staff.staffUid, online)
      return
    }
    await ensureSupportTicketsLane()
    const me = consoleAssignee()
    const staffCollection = supportStaffCollection()
    const emailKeyed = staffDocsKeyedByEmail(staffCollection)
    const onlineForLane = online && keepsDeskPresence()
    const laneEmail = emailKeyed ? me.email || rememberedDevStaffEmail() : me.email
    const staffDocId = supportStaffDocumentId(staff.staffUid, laneEmail)
    if (emailKeyed && !laneEmail) return
    if (laneEmail) rememberDevStaffEmail(laneEmail)
    const payload: Record<string, unknown> = {
      role: staff.staffRole,
      consoleRole: consoleAppRole() || null,
      supportDeskOnline: onlineForLane,
      lastSeenAt: serverTimestamp(),
      maxConcurrent: 5,
      consoleUserId: me.id || null,
      consoleEmail: laneEmail || null,
    }
    if (!isPlaceholderAgentName(staff.staffName)) payload.name = staff.staffName
    if (emailKeyed) {
      payload.firebaseUid = staff.staffUid
      payload.firebaseUids = deleteField()
    }
    await setDoc(doc(getDb(), staffCollection, staffDocId), payload, { merge: true })
    if (emailKeyed && !devStaffDocsConsolidated) {
      devStaffDocsConsolidated = true
      void consolidateDevStaffDocs().catch(() => {
        devStaffDocsConsolidated = false
      })
    }
    if (staffCollection !== legacyStaffCollection()) {
      await setDoc(
        doc(getDb(), legacyStaffCollection(), staff.staffUid),
        {
          supportDeskOnline: false,
          lastSeenAt: serverTimestamp(),
        },
        { merge: true },
      ).catch(() => undefined)
    }
  },

  startSupportDeskPresence() {
    if (staffDocsKeyedByEmail()) return () => undefined
    let timer: ReturnType<typeof setInterval> | null = null
    let started = false
    const beat = () => {
      this.setSupportDeskPresence(true).catch(() => undefined)
    }
    void this.ready().then(() => {
      if (started) return
      started = true
      beat()
      timer = setInterval(beat, PRESENCE_HEARTBEAT_MS)
    })
    return () => {
      started = false
      if (timer) clearInterval(timer)
      this.setSupportDeskPresence(false).catch(() => undefined)
    }
  },

  /**
   * While any online agent is under 5, every ticket takes the next ribbon seat,
   * oldest first. P0 and P1 leave the circle only when every online agent is at 5,
   * and then P0 is assigned before P1. P2 and P3 wait. The Cloud Function matches this.
   */
  async routeOpenUnassignedTickets(tickets: SupportTicket[]) {
    if (isSupportDemoMode()) return
    await ensureSupportTicketsLane()
    const staff = await this.ready()
    if (!staff.staffUid || !activeDeskSessions.has(staff.staffUid)) return
    if (!isSupportAgent()) return

    const agents = await loadStaffForRouting()
    const pendingLoads = new Map<string, number>()
    const open = tickets
      .filter((t) => {
        if (!ticketMatchesLane(t)) return false
        if (t.status !== 'open') return false
        if (t.escalated) return false
        if (t.assignmentStatus === 'cancelled') return false
        if (t.assignedStaffId && !isSharedDemoAssignment(t)) return false
        return true
      })
      .sort((a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0))

    const pastCap: SupportTicket[] = []
    for (const ticket of open) {
      if (this.ribbonHasRoom(agents, tickets, pendingLoads)) {
        await this.assignOneTicket(ticket, tickets, agents, pendingLoads)
        continue
      }
      if (ticket.priority === 'P0' || ticket.priority === 'P1') pastCap.push(ticket)
    }
    pastCap.sort(
      (a, b) =>
        priorityRank(a.priority) - priorityRank(b.priority) ||
        (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0),
    )
    for (const ticket of pastCap) {
      await this.assignOneTicket(ticket, tickets, agents, pendingLoads)
    }
  },

  ribbonHasRoom(agents: SupportStaffAgent[], tickets: SupportTicket[], pendingLoads: Map<string, number>) {
    return ribbonRoster(agents).some(
      (person) =>
        isRoutableSupportAgent(person) &&
        isFreshOnline(person) &&
        activeLoad(tickets, person, agents, pendingLoads) < (person.maxConcurrent || 5),
    )
  },

  async assignOneTicket(
    ticket: SupportTicket,
    allTickets: SupportTicket[],
    agents: SupportStaffAgent[],
    pendingLoads: Map<string, number>,
  ) {
    if (routeLocks.has(ticket.id)) return
    routeLocks.add(ticket.id)
    try {
      const seat = await this.routeOneUnassignedTicket(ticket, allTickets, agents, pendingLoads)
      if (seat) pendingLoads.set(seat, (pendingLoads.get(seat) || 0) + 1)
    } catch (err) {
      console.warn('[DriverSupport] route failed', ticket.id, err)
    } finally {
      routeLocks.delete(ticket.id)
    }
  },

  async routeOneUnassignedTicket(
    ticket: SupportTicket,
    allTickets: SupportTicket[],
    agents: SupportStaffAgent[],
    pendingLoads: Map<string, number>,
  ) {
    if (!ticketMatchesLane(ticket)) return ''
    const roster = ribbonRoster(agents)
    const online = (person: SupportStaffAgent) => isRoutableSupportAgent(person) && isFreshOnline(person)
    const underCap = (person: SupportStaffAgent) =>
      activeLoad(allTickets, person, agents, pendingLoads) < (person.maxConcurrent || 5)
    const onlineRoster = roster.filter(online)
    const roomOnRibbon = onlineRoster.some(underCap)
    const bypass = !roomOnRibbon && (ticket.priority === 'P0' || ticket.priority === 'P1')
    if (!roomOnRibbon && !bypass) return ''
    if (onlineRoster.length === 0) return ''

    const db = getDb()
    const ticketRef = ticketDoc(ticket.id, db)
    const circle = ribbonCircle(ticket)
    const rrRef = doc(db, 'app_config', supportRibbonDocId(circle))
    const me = consoleAssignee()
    const isP0 = ticket.priority === 'P0'

    const assignedSeat = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ticketRef)
      if (!snap.exists()) return ''
      const data = snap.data() as Record<string, unknown>
      if (String(data.status || '') !== 'open') return ''
      if (data.escalated) return ''
      if (data.assignedStaffId && String(data.assignedStaffId) !== 'demo_admin') return ''
      if (String(data.assignmentStatus || '') === 'cancelled') return ''

      let target: SupportStaffAgent | null
      if (bypass) {
        target = [...onlineRoster].sort(
          (a, b) =>
            activeLoad(allTickets, a, agents, pendingLoads) - activeLoad(allTickets, b, agents, pendingLoads) ||
            compareRibbon(a, b),
        )[0]!
      } else {
        const rr = await tx.get(rrRef)
        const lastId = rr.exists() ? String((rr.data() as Record<string, unknown>).lastAssignedStaffId || '') : ''
        const lastEmail = rr.exists()
          ? String((rr.data() as Record<string, unknown>).lastAssignedConsoleEmail || '')
          : ''
        target = nextOnRibbon(roster, lastId, lastEmail, agents, (person) => online(person) && underCap(person))
        if (!target) return ''
        tx.set(
          rrRef,
          {
            lastAssignedStaffId: target.uid,
            lastAssignedStaffName: target.name,
            lastAssignedConsoleEmail: target.consoleEmail || null,
            queueId: circle,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        )
      }

      const consoleUserId = target.consoleUserId || (target.uid === agent().staffUid ? me.id : '')
      const consoleEmail = target.consoleEmail || (target.uid === agent().staffUid ? me.email : '')

      tx.update(ticketRef, {
        status: 'accepted',
        assignmentStatus: 'assigned',
        assignedStaffId: target.uid,
        assignedStaffName: target.name,
        assignedStaffRole: target.role,
        assignedConsoleUserId: consoleUserId || null,
        assignedConsoleEmail: consoleEmail || null,
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(isP0 ? { p0ManagerAlert: true, p0ManagerAlertAt: serverTimestamp() } : {}),
      })
      const msgRef = doc(ticketMessages(ticket.id, db))
      tx.set(msgRef, {
        senderId: target.uid,
        senderName: target.name,
        senderType: 'staff',
        text: `${target.name} (${roleLabel(target.role)}) accepted this ticket. You can chat now.`,
        mediaType: 'text',
        createdAt: serverTimestamp(),
      })
      if (isP0) {
        const noteRef = doc(ticketDeskNotes(ticket.id, db))
        tx.set(noteRef, {
          authorId: target.uid,
          authorName: target.name,
          text: bypass
            ? `P0 assigned to ${target.name}. The manager can reply on this chat. The round robin seat did not move.`
            : `P0 assigned to ${target.name}. The manager can reply on this chat.`,
          createdAt: serverTimestamp(),
        })
      }
      return seatKey(target)
    })
    return assignedSeat || ''
  },

  isAdmin(role?: StaffFirebaseRole) {
    return (role || agent().staffRole) === 'admin'
  },

  /** Manager / admin / owner desk — one workspace, not the agent queue. */
  isManagementDesk(staffRole?: StaffFirebaseRole | string) {
    return isManagerDeskUser(staffRole)
  },

  async resolveTicket(ticketId: string, opts?: { resolutionCode: string; resolutionNote?: string }) {
    const staff = await this.ready()
    await this.assertCanAct(ticketId, staff)
    const code = opts?.resolutionCode || 'issue_resolved'
    const note = opts?.resolutionNote?.trim() || ''
    if (isSupportDemoMode()) {
      driverSupportDemoStore.resolve(ticketId, staff.staffName, code, note, staff)
      return
    }
    await updateDoc(ticketDoc(ticketId), {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      resolutionCode: code,
      resolutionNote: note || null,
    })
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName: staff.staffName,
      senderType: 'staff',
      text: `Ticket closed by ${staff.staffName}. The driver can rate now.`,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
    if (note) {
      await addDoc(ticketDeskNotes(ticketId), {
        authorId: staff.staffUid,
        authorName: staff.staffName,
        text: `Resolution (${code}): ${note}`,
        createdAt: serverTimestamp(),
      })
    }
  },

  async closeTicket(ticketId: string) {
    const staff = await this.ready()
    await this.assertCanAct(ticketId, staff)
    if (isSupportDemoMode()) {
      driverSupportDemoStore.close(ticketId)
      return
    }
    await updateDoc(ticketDoc(ticketId), {
      status: 'closed',
      closedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName: staff.staffName,
      senderType: 'staff',
      text: 'Ticket closed.',
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
  },

  async sendMessage(ticketId: string, text: string) {
    const staff = await this.ready()
    const trimmed = text.trim()
    if (!trimmed) return
    const ticket = await this.assertCanCompose(ticketId, staff)
    const ownsTicket = canActOnTicket(ticket, staff.staffUid, staff.staffRole)
    const asAdmin = canAdminReplyOnTicket(ticket, staff.staffRole) && !ownsTicket
    const body = asAdmin ? signedAdminReply(staff.staffName, trimmed) : trimmed
    const senderName = asAdmin ? adminDisplayName(staff.staffName) : staff.staffName
    if (isSupportDemoMode()) {
      driverSupportDemoStore.sendMessage(ticketId, body, {
        uid: staff.staffUid,
        name: senderName,
      })
      return
    }
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName,
      senderType: 'staff',
      text: body,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
    await updateDoc(ticketDoc(ticketId), {
      updatedAt: serverTimestamp(),
    })
    await stampFirstAgentResponse(ticketId)
  },

  async uploadMedia(ticketId: string, file: File, caption = '') {
    const staff = await this.ready()
    const ticket = await this.assertCanCompose(ticketId, staff)
    const ownsTicket = canActOnTicket(ticket, staff.staffUid, staff.staffRole)
    const asAdmin = canAdminReplyOnTicket(ticket, staff.staffRole) && !ownsTicket
    const signedCaption = asAdmin ? signedAdminReply(staff.staffName, caption) : caption.trim()
    const senderName = asAdmin ? adminDisplayName(staff.staffName) : staff.staffName
    if (isSupportDemoMode()) {
      driverSupportDemoStore.uploadMedia(ticketId, file, signedCaption, {
        uid: staff.staffUid,
        name: senderName,
      })
      return
    }
    const path = `${ticketsRoot()}/${ticketId}/${Date.now()}_${safeFileName(file.name)}`
    const sRef = storageRef(getFirebaseStorage(), path)
    try {
      await uploadBytes(sRef, file, {
        contentType: file.type || 'application/octet-stream',
      })
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : ''
      if (code === 'storage/unauthorized' || code === 'storage/permission-denied' || code.includes('unauthorized')) {
        throw new Error(
          `Storage permission denied. Firebase Console → Storage → Rules: allow read/write if request.auth != null for ${ticketsRoot()}/{ticketId}/{allPaths=**}.`,
        )
      }
      if (code === 'storage/bucket-not-found' || code === 'storage/retry-limit-exceeded') {
        throw new Error(
          `Firebase Storage not ready for ${getChatSupportProjectId()}. Enable Storage in Firebase Console.`,
        )
      }
      throw err
    }
    const mediaUrl = await getDownloadURL(sRef)
    const mediaType = guessMediaType(file)
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName,
      senderType: 'staff',
      text: signedCaption,
      mediaType,
      mediaUrl,
      fileName: file.name,
      mimeType: file.type || '',
      createdAt: serverTimestamp(),
    })
    await updateDoc(ticketDoc(ticketId), {
      updatedAt: serverTimestamp(),
    })
    await stampFirstAgentResponse(ticketId)
  },

  matchesRoleCategory(ticket: SupportTicket, role?: StaffFirebaseRole) {
    return ticketVisibleToRole(ticket, role || agent().staffRole)
  },

  filterQueue(tickets: SupportTicket[], role?: StaffFirebaseRole) {
    return sortDesk(
      tickets.filter((t) => {
        if (t.escalated) return false
        if (t.status !== 'open') return false
        if (t.assignmentStatus === 'cancelled') return false
        if (t.assignedStaffId && !isSharedDemoAssignment(t)) return false
        return this.matchesRoleCategory(t, role)
      }),
    )
  },

  filterAssigned(tickets: SupportTicket[], staffId?: string) {
    const id = staffId || agent().staffUid
    return sortDesk(
      tickets.filter((t) => {
        if (t.status !== 'accepted' && t.status !== 'open') return false
        return ticketOwnedByCurrentConsoleUser(t, id)
      }),
    )
  },

  filterMine(tickets: SupportTicket[], staffId?: string) {
    const id = staffId || agent().staffUid
    return sortDesk(
      tickets.filter((t) => {
        if (t.status === 'resolved' || t.status === 'closed') return false
        if (t.assignmentStatus === 'cancelled') return false
        if (ticketOwnedByCurrentConsoleUser(t, id)) return true
        return agentEscalatedThis(t, id)
      }),
    )
  },

  filterCompleted(tickets: SupportTicket[], staffId?: string) {
    const id = staffId || agent().staffUid
    return sortDesk(
      tickets.filter((t) => {
        if (t.status !== 'resolved' && t.status !== 'closed') return false
        if (ticketOwnedByCurrentConsoleUser(t, id)) return true
        return agentEscalatedThis(t, id)
      }),
    )
  },

  /** Active escalations still needing a team manager. */
  filterEscalated(tickets: SupportTicket[]) {
    return sortDesk(
      tickets.filter(
        (t) => t.escalated && t.assignmentStatus !== 'cancelled' && t.status !== 'resolved' && t.status !== 'closed',
      ),
    )
  },

  /** Escalations that were resolved or closed. */
  filterEscalatedClosed(tickets: SupportTicket[]) {
    return sortDesk(
      tickets.filter(
        (t) => t.escalated && t.assignmentStatus !== 'cancelled' && (t.status === 'resolved' || t.status === 'closed'),
      ),
    )
  },

  filterManageOpen(tickets: SupportTicket[]) {
    return sortDesk(tickets.filter((t) => t.status === 'open' || t.status === 'accepted'))
  },

  /** Live queue for managers: open/accepted and not escalated. */
  filterManageOpenLive(tickets: SupportTicket[]) {
    return sortDesk(
      tickets.filter((t) => {
        if (t.escalated) return false
        if (t.assignmentStatus === 'cancelled') return false
        return t.status === 'open' || t.status === 'accepted'
      }),
    )
  },

  /** Finished cases (resolved or closed), including former escalations. */
  filterManageCompleted(tickets: SupportTicket[]) {
    return sortDesk(
      tickets.filter((t) => {
        if (t.assignmentStatus === 'cancelled') return false
        return t.status === 'resolved' || t.status === 'closed'
      }),
    )
  },

  filterManageResolved(tickets: SupportTicket[]) {
    return sortDesk(tickets.filter((t) => t.status === 'resolved'))
  },

  filterManageClosed(tickets: SupportTicket[]) {
    return sortDesk(tickets.filter((t) => t.status === 'closed'))
  },

  isEscalationHandler(role?: StaffFirebaseRole) {
    return roleIsEscalationHandler(role)
  },

  canResolveOrClose(ticket: SupportTicket | null, staffUid: string, staffRole: StaffFirebaseRole) {
    if (!ticket) return false
    if (ticket.status === 'closed' || ticket.status === 'resolved') return false
    if (ticket.assignmentStatus === 'cancelled') return false
    return canActOnTicket(ticket, staffUid, staffRole) || canAdminReplyOnTicket(ticket, staffRole)
  },

  canTakeEscalation(ticket: SupportTicket | null, staffUid: string, staffRole: StaffFirebaseRole) {
    return canTakeEscalationTicket(ticket, staffUid, staffRole)
  },

  /** View the thread only — no driver reply, notes, resolve, or close. */
  isEscalationObserver(ticket: SupportTicket | null, staffUid: string, staffRole: StaffFirebaseRole) {
    if (!ticket?.escalated) return false
    if (canActOnTicket(ticket, staffUid, staffRole)) return false
    if (canAdminReplyOnTicket(ticket, staffRole)) return false
    if (canTakeEscalationTicket(ticket, staffUid, staffRole)) return false
    return true
  },

  async takeEscalation(ticketId: string) {
    const staff = await this.ready()
    if (isSupportAgent() && !isSupportDemoMode()) {
      throw new Error('Support agents cannot assign escalated tickets.')
    }
    if (!isManagerDeskUser(staff.staffRole)) {
      throw new Error('Only a team manager can assign this escalation.')
    }
    if (isSupportDemoMode()) {
      const current = driverSupportDemoStore.getTicket(ticketId)
      if (!canTakeEscalationTicket(current, staff.staffUid, staff.staffRole)) {
        throw new Error('This escalation is already assigned.')
      }
      driverSupportDemoStore.assign(
        ticketId,
        { uid: staff.staffUid, name: staff.staffName, role: staff.staffRole },
        { staffUid: staff.staffUid, staffName: staff.staffName },
      )
      const taken = driverSupportDemoStore.getTicket(ticketId)
      if (taken) taken.escalationTakenAt = new Date()
      return
    }
    const ticketRef = ticketDoc(ticketId)
    await runTransaction(getDb(), async (tx) => {
      const snap = await tx.get(ticketRef)
      if (!snap.exists()) throw new Error('Ticket not found')
      const ticket = mapTicket(snap.id, snap.data() as Record<string, unknown>)
      if (!canTakeEscalationTicket(ticket, staff.staffUid, staff.staffRole)) {
        throw new Error('This escalation is already assigned to a manager.')
      }
      const me = consoleAssignee()
      tx.update(ticketRef, {
        status: 'accepted',
        assignmentStatus: 'assigned',
        assignedStaffId: staff.staffUid,
        assignedStaffName: staff.staffName,
        assignedStaffRole: staff.staffRole,
        assignedConsoleUserId: me.id || null,
        assignedConsoleEmail: me.email || null,
        escalationTakenAt: serverTimestamp(),
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    })
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid,
      senderName: staff.staffName,
      senderType: 'staff',
      text: `${staff.staffName} (${roleLabel(staff.staffRole)}) assigned this escalation to themselves.`,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
  },

  isNewAssigned(ticket: SupportTicket, withinMs = 10 * 60 * 1000) {
    const t = ticket.acceptedAt || ticket.updatedAt
    if (!t) return false
    if (ticket.status !== 'accepted') return false
    return Date.now() - t.getTime() < withinMs
  },

  isNewMine(ticket: SupportTicket, withinMs = 10 * 60 * 1000) {
    return this.isNewAssigned(ticket, withinMs)
  },

  isMine(ticket: SupportTicket, staffUid?: string) {
    return ticketOwnedByCurrentConsoleUser(ticket, staffUid || agent().staffUid)
  },

  isQueueClaimable(ticket: SupportTicket) {
    if (!isSupportAgent()) return false
    if (ticket.escalated) return false
    if (ticket.status !== 'open') return false
    if (ticket.assignmentStatus === 'cancelled') return false
    return !ticket.assignedStaffId || isSharedDemoAssignment(ticket)
  },

  /** A driver message after resolve/close puts the ticket back on the open lists. */
  async syncFinishedTickets(tickets: SupportTicket[]) {
    for (const ticket of tickets) {
      if (ticket.status !== 'resolved' && ticket.status !== 'closed') continue
      if (ticket.assignmentStatus === 'cancelled') continue
      const finished = finishedAtMs(ticket)
      const activityAfter = finished > 0 && (ticket.updatedAt?.getTime() || 0) > finished + 1500
      const explicitReopen = finished > 0 && (ticket.reopenedAt?.getTime() || 0) > finished
      const maybeClose = ticket.status === 'resolved' && hasDriverRating(ticket)
      if (!activityAfter && !explicitReopen && !maybeClose) continue
      const lock = `${ticket.id}:${finished}`
      if (ratingCloseLocks.has(lock)) continue
      ratingCloseLocks.add(lock)
      try {
        const reopened = explicitReopen || (activityAfter && (await driverMessagedAfter(ticket.id, finished)))
        if (reopened) {
          await this.promoteReopenedTicket(ticket.id, finished)
          continue
        }
        if (maybeClose) await this.closeTicketAfterDriverRating(ticket.id)
      } catch {
        ratingCloseLocks.delete(lock)
      }
    }
  },

  async promoteReopenedTicket(ticketId: string, finishedMs: number) {
    await this.ready()
    if (isSupportDemoMode()) return
    const staff = agent()
    const ref = ticketDoc(ticketId)
    const noticeRef = doc(ticketMessages(ticketId))
    await runTransaction(getDb(), async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) return
      const data = snap.data() as Record<string, unknown>
      const status = normalizeStatus(data.status)
      if (status !== 'resolved' && status !== 'closed') return
      if (String(data.assignmentStatus || '') === 'cancelled') return
      const finished = Math.max(toDate(data.resolvedAt)?.getTime() || 0, toDate(data.closedAt)?.getTime() || 0)
      if (finished !== finishedMs) return
      const assigned = Boolean(data.assignedStaffId) && String(data.assignmentStatus || '') === 'assigned'
      tx.update(ref, {
        status: assigned ? 'accepted' : 'open',
        escalated: false,
        reopenedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      tx.set(noticeRef, {
        senderId: staff.staffUid || 'system',
        senderName: 'System',
        senderType: 'staff',
        text: REOPEN_NOTICE,
        mediaType: 'text',
        createdAt: serverTimestamp(),
      })
    })
  },

  async closeTicketAfterDriverRating(ticketId: string) {
    await this.ready()
    if (isSupportDemoMode()) {
      driverSupportDemoStore.close(ticketId)
      return
    }
    const staff = agent()
    const ref = ticketDoc(ticketId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return
    const data = snap.data() as Record<string, unknown>
    const status = normalizeStatus(data.status)
    if (status === 'closed') return
    if (status !== 'resolved') return
    const stars = Number((data.rating as { stars?: unknown } | undefined)?.stars)
    if (!Number.isFinite(stars) || stars <= 0) return
    const notice = 'Ticket closed after driver rating.'
    const existing = await getDocs(query(ticketMessages(ticketId), where('text', '==', notice), limit(1)))
    await updateDoc(ref, {
      status: 'closed',
      closedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    if (!existing.empty) return
    await addDoc(ticketMessages(ticketId), {
      senderId: staff.staffUid || 'system',
      senderName: 'System',
      senderType: 'staff',
      text: notice,
      mediaType: 'text',
      createdAt: serverTimestamp(),
    })
  },

  canTransferTicket(ticket: SupportTicket | null, staffRole?: StaffFirebaseRole | string) {
    return canReassignTicket(ticket, staffRole)
  },

  transferTargets(agents: SupportStaffAgent[], ticket: SupportTicket | null) {
    const bySeat = new Map<string, SupportStaffAgent>()
    const assignedEmail = ticket?.assignedConsoleEmail?.trim().toLowerCase() || ''
    for (const person of agents) {
      if (!isLiveDeskAgentTarget(person, ticket?.assignedStaffId)) continue
      const email = person.consoleEmail?.trim().toLowerCase() || ''
      if (assignedEmail && email === assignedEmail) continue
      const name = transferSeatLabel(person)
      if (isPlaceholderAgentName(name)) continue
      const seat = name === person.name ? person : { ...person, name }
      const key = email || name.toLowerCase()
      const prev = bySeat.get(key)
      if (!prev || preferAssignSeat(seat, prev)) bySeat.set(key, seat)
    }
    return [...bySeat.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    )
  },

  canComposeChat(ticket: SupportTicket | null, staffUid: string, staffRole: StaffFirebaseRole) {
    return canActOnTicket(ticket, staffUid, staffRole) || canAdminReplyOnTicket(ticket, staffRole)
  },

  canAdminReplyAnyTicket(ticket: SupportTicket | null, staffRole?: StaffFirebaseRole) {
    return canAdminReplyOnTicket(ticket, staffRole)
  },

  adminChatSignatureLabel() {
    return adminReplyHeading(agent().staffName).replace(/ replied$/, '')
  },
}
